import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IoTimeOutline } from 'react-icons/io5';
import './CommunityHero.css';

/**
 * Count a number up to its new value when it changes.
 * Inline, unlike AnimatedCounter, which is a whole card.
 */
function useCountUp(value, duration = 900) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const frameRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    if (from === value) return undefined;

    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      fromRef.current = value;
      setDisplay(value);
      return undefined;
    }

    let start = null;
    const step = (timestamp) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (value - from) * eased));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = value;
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      fromRef.current = value;
    };
  }, [value, duration]);

  return display;
}

const formatNumber = (value) => new Intl.NumberFormat().format(value);

/**
 * The community read-through, stated plainly (WS1).
 *
 * "Together we've read the Bible through N times. M chapters to the next."
 * This is the one number that makes every log feel collective, so it is the
 * first thing on the page.
 */
const CommunityHero = ({ stats, challenge }) => {
  const completions = stats?.completeBibleReads ?? 0;
  const remaining = stats?.chaptersToNextCompletion ?? 0;

  const shownCompletions = useCountUp(completions);
  const shownRemaining = useCountUp(remaining);

  if (!stats) return null;

  return (
    <section className="community-hero">
      {challenge && (
        <div className="hero-challenge">
          <span className="hero-challenge-name">{challenge.name}</span>
          {typeof challenge.daysRemaining === 'number' && (
            <span className="hero-countdown">
              <IoTimeOutline size={14} aria-hidden="true" />
              {challenge.daysRemaining === 0
                ? 'Last day'
                : `${challenge.daysRemaining} day${challenge.daysRemaining === 1 ? '' : 's'} left`}
            </span>
          )}
        </div>
      )}

      <p className="hero-scope">{stats.year && stats.year !== 'all' ? `${stats.year} COMMUNITY READING` : 'ALL-TIME COMMUNITY READING'}</p>
      <h2 className="hero-headline">
        Together we&apos;ve read the Bible through{' '}
        <strong className="hero-number">{formatNumber(shownCompletions)}</strong>{' '}
        {completions === 1 ? 'time' : 'times'}.
      </h2>

      <p className="hero-subline">{formatNumber(stats.totalChaptersRead ?? 0)} chapters read · {formatNumber(stats.participatingReaders ?? 0)} readers participating</p>
      {stats.rawChapters > 0 && <p>{formatNumber(stats.rawChapters)} chapters logged by count are included in reading totals; the map tracks identified chapters.</p>}
      <p className="hero-subline">
        <strong className="hero-remaining">{formatNumber(shownRemaining)}</strong>{' '}
        {remaining === 1 ? 'chapter' : 'chapters'} to the next.
      </p>
    </section>
  );
};

CommunityHero.propTypes = {
  stats: PropTypes.shape({
    year: PropTypes.string,
    totalChaptersRead: PropTypes.number,
    participatingReaders: PropTypes.number,
    rawChapters: PropTypes.number,
    completeBibleReads: PropTypes.number,
    chaptersToNextCompletion: PropTypes.number
  }),
  challenge: PropTypes.shape({
    name: PropTypes.string,
    daysRemaining: PropTypes.number
  })
};

export default CommunityHero;
